import supabase from '../supabase/supabase';

const TABLES = {
  courses: 'hms_academy_courses_1715725000000',
  versions: 'hms_academy_course_versions_1715725000000',
  units: 'hms_academy_learning_units_1715725000000',
  assessments: 'hms_academy_assessments_1715728000000',
  questions: 'hms_academy_assessment_questions_1715728000000',
  options: 'hms_academy_assessment_options_1715728000000'
};

const ASSESSMENT_FIELDS = [
  'id',
  'assessment_key',
  'title',
  'description',
  'required_for_completion',
  'pass_threshold',
  'max_attempts',
  'grading_mode',
  'status'
].join(',');

function createServiceError(code, message, cause) {
  const error = new Error(message);
  error.code = code;
  if (cause) error.cause = cause;
  return error;
}

function unwrap(result, label) {
  if (result.error) {
    throw createServiceError(
      'SUPABASE_ERROR',
      `Academy ${label} query failed: ${result.error.message}`,
      result.error
    );
  }

  return result.data;
}

function requireOne(rows, code, label) {
  if (rows.length === 0) {
    throw createServiceError(code, `Published Academy ${label} was not found.`);
  }

  if (rows.length !== 1) {
    throw createServiceError(
      'SUPABASE_ERROR',
      `Expected one published Academy ${label}, found ${rows.length}.`
    );
  }

  return rows[0];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function adaptUnit(row) {
  const payload = asObject(row.content_payload);
  const concepts = asArray(payload.concepts);
  const sources = asArray(payload.sources);

  return {
    id: row.unit_key,
    unitKey: row.unit_key,
    title: row.title,
    description: row.description || '',
    objective: typeof payload.objective === 'string'
      ? payload.objective
      : row.description || '',
    displayOrder: row.display_order,
    duration: row.estimated_duration_minutes,
    durationMinutes: row.estimated_duration_minutes,
    isRequired: row.is_required,
    overview: typeof payload.overview === 'string' ? payload.overview : '',
    concepts,
    example: typeof payload.example === 'string' ? payload.example : '',
    reflection: typeof payload.reflection === 'string' ? payload.reflection : '',
    exercise: typeof payload.exercise === 'string' ? payload.exercise : '',
    keyMessage: typeof payload.keyMessage === 'string' ? payload.keyMessage : '',
    explanation: typeof payload.explanation === 'string'
      ? payload.explanation
      : '',
    questions: asArray(payload.questions),
    facilitatorActivity: typeof payload.facilitatorActivity === 'string'
      ? payload.facilitatorActivity
      : '',
    watchFor: typeof payload.watchFor === 'string' ? payload.watchFor : '',
    transition: typeof payload.transition === 'string' ? payload.transition : '',
    hazards: asArray(payload.hazards),
    protectiveFactors: asArray(payload.protectiveFactors),
    sources
  };
}

function adaptAssessment(assessmentRow, questionRows, optionRows) {
  const optionsByQuestion = new Map();

  optionRows.forEach((option) => {
    const options = optionsByQuestion.get(option.question_id) || [];
    options.push({
      id: option.id,
      optionKey: option.option_key,
      text: option.option_text,
      displayOrder: option.display_order
    });
    optionsByQuestion.set(option.question_id, options);
  });

  return {
    id: assessmentRow.id,
    assessmentKey: assessmentRow.assessment_key,
    title: assessmentRow.title,
    description: assessmentRow.description,
    requiredForCompletion: assessmentRow.required_for_completion,
    passThreshold: assessmentRow.pass_threshold,
    maxAttempts: assessmentRow.max_attempts,
    gradingMode: assessmentRow.grading_mode,
    status: assessmentRow.status,
    questions: questionRows.map((question) => ({
      id: question.id,
      questionKey: question.question_key,
      type: question.question_type,
      prompt: question.question_text,
      displayOrder: question.display_order,
      points: question.points,
      required: question.required,
      options: optionsByQuestion.get(question.id) || []
    }))
  };
}

function adaptCourse(courseRow, versionRow, unitRows, assessment) {
  return {
    courseKey: courseRow.course_key,
    version: versionRow.version,
    languageCode: versionRow.language_code,
    title: versionRow.title,
    professionalArea: courseRow.professional_area || versionRow.professional_area || '',
    description: versionRow.description || '',
    learningObjectives: asArray(versionRow.learning_objectives),
    targetAudience: versionRow.target_audience || '',
    audience: versionRow.target_audience || '',
    duration: versionRow.estimated_duration_minutes,
    durationMinutes: versionRow.estimated_duration_minutes,
    deliveryCapabilities: asArray(versionRow.delivery_capabilities),
    certificateEnabled: versionRow.certificate_enabled ?? false,
    certificateIssuerDisplayName:
      versionRow.certificate_issuer_display_name || '',
    certificateDisplayTitle: versionRow.certificate_display_title || '',
    modules: unitRows.map(adaptUnit),
    assessment
  };
}

export async function getPublishedAcademyCourse(
  courseKey,
  { version = '1.0', languageCode = 'nb' } = {}
) {
  const courseRows = unwrap(
    await supabase
      .from(TABLES.courses)
      .select('id,course_key,title,professional_area,status,organization_id')
      .is('organization_id', null)
      .eq('course_key', courseKey)
      .eq('status', 'approved')
      .limit(2),
    'course'
  );

  const course = requireOne(courseRows, 'COURSE_NOT_FOUND', 'course');

  const versionRows = unwrap(
    await supabase
      .from(TABLES.versions)
      .select([
        'id',
        'course_id',
        'version',
        'language_code',
        'title',
        'description',
        'learning_objectives',
        'target_audience',
        'estimated_duration_minutes',
        'delivery_capabilities',
        'professional_area',
        'certificate_enabled',
        'certificate_issuer_display_name',
        'certificate_display_title',
        'status'
      ].join(','))
      .eq('course_id', course.id)
      .eq('version', version)
      .eq('language_code', languageCode)
      .eq('status', 'approved')
      .limit(2),
    'course version'
  );

  const courseVersion = requireOne(
    versionRows,
    'VERSION_NOT_FOUND',
    'course version'
  );

  const unitRows = unwrap(
    await supabase
      .from(TABLES.units)
      .select([
        'id',
        'unit_key',
        'title',
        'description',
        'display_order',
        'content_payload',
        'estimated_duration_minutes',
        'is_required',
        'status'
      ].join(','))
      .eq('course_version_id', courseVersion.id)
      .eq('status', 'approved')
      .order('display_order', { ascending: true }),
    'learning units'
  );

  const assessmentRows = unwrap(
    await supabase
      .from(TABLES.assessments)
      .select(ASSESSMENT_FIELDS)
      .eq('course_version_id', courseVersion.id)
      .in('status', ['published', 'active'])
      .limit(2),
    'assessment'
  );

  const assessmentRow = requireOne(
    assessmentRows,
    'ASSESSMENT_NOT_FOUND',
    'assessment'
  );

  const questionRows = unwrap(
    await supabase
      .from(TABLES.questions)
      .select([
        'id',
        'assessment_id',
        'question_key',
        'question_type',
        'question_text',
        'display_order',
        'points',
        'required'
      ].join(','))
      .eq('assessment_id', assessmentRow.id)
      .order('display_order', { ascending: true }),
    'assessment questions'
  );

  let optionRows = [];

  if (questionRows.length > 0) {
    optionRows = unwrap(
      await supabase
        .from(TABLES.options)
        .select('id,question_id,option_key,option_text,display_order')
        .in('question_id', questionRows.map((question) => question.id))
        .order('question_id', { ascending: true })
        .order('display_order', { ascending: true }),
      'assessment options'
    );
  }

  const assessment = adaptAssessment(
    assessmentRow,
    questionRows,
    optionRows
  );

  return adaptCourse(course, courseVersion, unitRows, assessment);
}